import {FC, useState} from 'react'
import {ID, isTaskOverdue} from '../../../../../_metronic/helpers'
import {useListView} from '../../core/ListViewProvider'
import {useSearchParams} from 'react-router-dom'
import {Task} from '../../core/_models'
import {UsersSymbolGroupColumn} from '../../../project/components/tasks-list/UsersSymbolGroupColumn'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'

type Props = {
  task: Task
  showDeliveryFooter?: boolean
  onDragStart: () => void
  onDragLeave: () => void
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void
}

const KanbanCard: FC<Props> = ({task, showDeliveryFooter = false, onDragStart, onDragOver, onDragLeave}) => {
  const {setTaskIdForUpdate} = useListView()
  const [showModal, setShowModal] = useState(false)
  const [action, setAction] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()
  const [taskByIdURL, setTaskByIdURL] = useState<ID>()

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60)
    const mins = Math.round(minutes % 60)

    if (hours === 0) {
      return `${mins}m`
    } else if (mins === 0) {
      return `${hours}h`
    } else {
      return `${hours}:${mins.toString().padStart(2, '0')}`
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critica':
        return 'badge-danger'
      case 'alta':
        return 'badge-warning'
      case 'media':
        return 'badge-info'
      case 'baixa':
        return 'badge-success'
      default:
        return 'badge-secondary'
    }
  }

  const getPriorityText = (priority: string) => {
    switch (priority) {
      case 'critica':
        return 'Crítica'
      case 'alta':
        return 'Alta'
      case 'media':
        return 'Média'
      case 'baixa':
        return 'Baixa'
      default:
        return ''
    }
  }

  const openEditModal = () => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('taskId', task.id!.toString()!)
    setSearchParams(newParams)
    setTaskIdForUpdate(task.id)
    setTaskByIdURL(task.id)
    setShowModal(true)
    setAction(true)
  }

  return (
    <div className='kanban-demo-card mb-3' onClick={openEditModal}>
      <div
        className='card border-0 shadow-sm hover-shadow kanban-card-hover'
        draggable
        onDragStart={onDragStart}
        onDragOver={(e) => onDragOver(e)}
        onDragLeave={onDragLeave}
      >
        <div className='card-body p-3'>
          {/* Header com título e prioridade */}
          <div className='d-flex justify-content-between align-items-start mb-2'>
            <h6 className='card-title mb-0 fs-5 fw-bold text-dark'>{task.name}</h6>
            <span className={`badge ${getPriorityColor(task.priority!)} fs-7`}>
              {getPriorityText(task.priority!)}
            </span>
          </div>

          {/* Informações do projeto */}
          <div className='d-flex mb-2' style={{minWidth: 0}}>
            <small
              className='d-flex align-items-center text-muted fs-7 flex-grow-1'
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                minWidth: 0,
              }}
            >
              <i className='bi bi-folder fs-6 me-1'></i>
              <span>{task.project_name}</span>
            </small>
          </div>

          {/* Ícones de funcionalidades */}
          <div className='d-flex mb-2' style={{minWidth: 0}}>
            <span
              className='badge badge-secondary fs-9 d-flex align-items-center'
              style={{alignSelf: 'start'}}
            >
              {/* @ts-ignore */}
              {task.project_stage?.name}
            </span>
          </div>

          <div className='d-flex gap-1 mb-2'>
            {!!task.tags_count && task.tags_count > 0 && (
              <OverlayTrigger
                placement='top'
                delay={{show: 50, hide: 100}}
                overlay={<Tooltip>Esta tarefa possui uma tag associada.</Tooltip>}
              >
                <span className='text-primary'>
                  <i className='bi bi-tag-fill fs-6'></i>
                </span>
              </OverlayTrigger>
            )}

            {!!task.task_checklists_count && task.task_checklists_count > 0 && (
              <OverlayTrigger
                placement='top'
                delay={{show: 50, hide: 100}}
                overlay={<Tooltip>Esta tarefa possui checklist de itens.</Tooltip>}
              >
                <span className='text-success'>
                  <i className='bi bi-check2-square fs-6'></i>
                </span>
              </OverlayTrigger>
            )}

            {!!task.expected_date && (
              <>
                {isTaskOverdue(task.expected_date, {
                  delivered: showDeliveryFooter,
                  endDate: task.end_date ?? undefined,
                }) ? (
                  <OverlayTrigger
                    placement='top'
                    delay={{show: 50, hide: 100}}
                    overlay={<Tooltip>Esta tarefa está atrasada.</Tooltip>}
                  >
                    <span
                      className={'calendar-overdue'}
                      style={{animation: 'blink-red 1.5s infinite'}}
                    >
                      <i className='bi bi-calendar-event fs-6'></i>
                    </span>
                  </OverlayTrigger>
                ) : (
                  <OverlayTrigger
                    placement='top'
                    delay={{show: 50, hide: 100}}
                    overlay={<Tooltip>Esta tarefa possui prazo definido</Tooltip>}
                  >
                    <span className={'text-warning'}>
                      <i className='bi bi-calendar-event fs-6'></i>
                    </span>
                  </OverlayTrigger>
                )}
              </>
            )}
          </div>

          {/* Footer com ID, usuários e tempo */}
          <div className='d-flex justify-content-between align-items-center'>
            <div className='d-flex align-items-center gap-2' style={{minWidth: 0, flex: 1}}>
              <small className='text-muted fs-7'>#{task.id}</small>
              <div className='d-flex align-items-center gap-1 ms-2' style={{flexShrink: 0}}>
                {/* @ts-ignore */}
                <UsersSymbolGroupColumn
                  isKanban={true}
                  isReadonly={true}
                  // @ts-ignore
                  users={task.users ?? []}
                  usersAll={[]}
                />
              </div>
            </div>
            {!!task.total_time && (
              <small className='d-flex align-items-center text-muted fs-7' style={{flexShrink: 0}}>
                <i className='bi bi-clock me-1 fs-6'></i>
                {formatTime(task.total_time / 60)}
              </small>
            )}
          </div>

          {/* Data de entrega - apenas na coluna Entregue */}
          {showDeliveryFooter && task.end_date != null && (
            <div className='mt-2 pt-2 border-top' style={{overflow: 'visible', minHeight: 'auto'}}>
              <small
                className='text-success fs-7'
                style={{whiteSpace: 'nowrap', overflow: 'visible'}}
              >
                <i className='bi bi-check-circle me-1 fs-6'></i>
                Data de entrega: {task.end_date}
              </small>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export {KanbanCard}
